import defaultFetch from 'isomorphic-unfetch';
import {
  createQueryStringSerializer,
  type QueryStringSerializer,
} from '@/scopes/common/infrastructure/query-strings/queryStringSerializer';
import { createDestinationMapResponseMapper, type DestinationMapResponseMapper } from './destinationMapResponseMapper';
import { type AccoDetailsResponseMapper, createAccoDetailsResponseMapper } from './accoDetailsResponseMapper';
import type { AccoDetailsAPIResponse, NearbyAccosAPIResponse } from './types';

interface Dependencies {
  env: {
    NEXT_PUBLIC_API_BASE_URL: string;
  };
  fetch: typeof defaultFetch;
  queryStringSerializer: QueryStringSerializer;
  destinationMapResponseMapper: DestinationMapResponseMapper;
  accoDetailsResponseMapper: AccoDetailsResponseMapper;
}

export interface GetDestinationMapParameters {
  currentAccoId: string;
}

export const createDestinationMapAPIClient = (dependencies: Partial<Dependencies> = {}) => {
  const {
    env = {
      NEXT_PUBLIC_API_BASE_URL: process.env.NEXT_PUBLIC_API_BASE_URL,
    },
    fetch = defaultFetch,
    queryStringSerializer = createQueryStringSerializer(),
    destinationMapResponseMapper = createDestinationMapResponseMapper(),
    accoDetailsResponseMapper = createAccoDetailsResponseMapper(),
  } = dependencies as Dependencies;

  async function getNearbyAccos({ currentAccoId }: GetDestinationMapParameters) {
    const queryString = queryStringSerializer.serialize({ accommodationId: currentAccoId });

    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/AccommodationGeoApi/GetNearbyAccommodationsByAccommodationId?${queryString}`,
      {
        referrerPolicy: 'strict-origin-when-cross-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );

    const data = (await response.json()) as NearbyAccosAPIResponse;
    return destinationMapResponseMapper.map(data);
  }

  async function getAccoDetails({ accoId }: { accoId: string }) {
    const queryString = queryStringSerializer.serialize({ accommodationId: accoId });

    const response = await fetch(
      `${env.NEXT_PUBLIC_API_BASE_URL}/AccommodationGeoApi/GetAccommodationDetailsById?${queryString}`,
      {
        referrerPolicy: 'strict-origin-when-cross-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      }
    );

    const data = (await response.json()) as AccoDetailsAPIResponse[];

    return accoDetailsResponseMapper.map(data[0]);
  }

  return { getNearbyAccos, getAccoDetails };
};
